import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../App';
import AccessDeniedModal from '../../common/AccessDeniedModal';
import './accountAdmin.css';
import { formatDateVNDate } from '../../../utils/formatDate';
import Select from 'react-select';
// ...existing code... (internal ClassesEditor and GradeClassesPanel are defined later in this file)

function Modal({ show, title, onClose, children }) {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h4>{title}</h4>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// Small wrapper around react-select to keep the file using simple scalar values
function RSSelect({ options = [], value, onChange, placeholder = '', isClearable = false, emptyValue = '' }) {
  // normalize incoming options (allow array of strings or {value,label})
  const opts = options.map(o => (o && typeof o === 'object' && o.value !== undefined) ? o : { value: o, label: String(o) });
  const selected = opts.find(o => String(o.value) === String(value)) || null;
  // Use a portal into document.body so the dropdown menu isn't clipped or causes parent scroll
  const menuPortalTarget = (typeof document !== 'undefined') ? document.body : undefined;

  // compact styles: reduce height, padding and font size so the control doesn't look oversized
  // target showing ~6 options then scroll: set menuList max height accordingly
  const OPTION_HEIGHT = 28; // px per option (approx)
  const VISIBLE_OPTIONS = 6;
  const MENU_MAX_HEIGHT = OPTION_HEIGHT * VISIBLE_OPTIONS; // e.g., 168px

  const compactStyles = {
    // make control reasonably short; use explicit width but allow it to shrink on small screens
    control: base => ({ ...base, minHeight: 32, height: 32, fontSize: 13, borderRadius: 6, padding: '0 6px', width: 180, maxWidth: '100%' }),
    valueContainer: base => ({ ...base, padding: '0 6px', height: 32 }),
    indicatorsContainer: base => ({ ...base, height: 32 }),
    input: base => ({ ...base, margin: 0, padding: 0 }),
    singleValue: base => ({ ...base, margin: 0 }),
    placeholder: base => ({ ...base, margin: 0, color: '#666', fontSize: 13 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({ ...base, minWidth: 180, width: 180 }),
    menuList: base => ({ ...base, maxHeight: MENU_MAX_HEIGHT, overflowY: 'auto' }),
    option: base => ({ ...base, fontSize: 13, padding: '4px 8px', minHeight: OPTION_HEIGHT, display: 'flex', alignItems: 'center' })
  };

  return (
    <Select
      options={opts}
      value={selected}
      onChange={opt => onChange(opt ? opt.value : emptyValue)}
      isClearable={isClearable}
      placeholder={placeholder}
      menuPortalTarget={menuPortalTarget}
      menuPosition={'fixed'}
      menuMaxHeight={MENU_MAX_HEIGHT}
      styles={compactStyles}
      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
      classNamePrefix="rs"
    />
  );
}

function authHeader() { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }; }

export default function AccountAdmin() {
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [schools, setSchools] = useState([]);
  const [editingSchoolId, setEditingSchoolId] = useState(null);
  const [editingSchoolName, setEditingSchoolName] = useState('');
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolsEditorOpen, setSchoolsEditorOpen] = useState(false);

  useEffect(() => {
    // Admin: fetch full schools list. Mod: we will use their assigned school only.
    if (!user) return;
    if (user.role === 'Admin') fetchSchools();
    if (user.role === 'Mod' && user.school_id) {
      setSelectedSchool({ id: user.school_id, school_name: user.school_name || 'My School' });
    }
  }, [user]);

  // NOTE: school-year management was moved to a dedicated admin page.

  // read initial context from URL or localStorage
  const location = useLocation();
  const [initialTabFromUrl, setInitialTabFromUrl] = useState(null);
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const t = sp.get('tab');
      if (t) setInitialTabFromUrl(t);
    } catch (e) { /* ignore */ }
    // check localStorage for admin_selected_school (set by dashboard)
    try {
      const v = localStorage.getItem('admin_selected_school');
      if (v && !selectedSchool) {
        // if Admin, we need to wait until schools list is loaded; otherwise set simple placeholder
        if (user && user.role === 'Admin') {
          // attempt to resolve after schools load in another effect
        } else {
          setSelectedSchool({ id: Number(v), school_name: null });
        }
      }
    } catch (e) { }
  }, [location.search]);

  // when schools list is loaded, apply any persisted selection from localStorage
  useEffect(() => {
    if (!schools || !schools.length) return;
    try {
      const v = localStorage.getItem('admin_selected_school');
      if (v) {
        const sid = Number(v);
        const found = schools.find(s => Number(s.id) === sid);
        if (found) setSelectedSchool(found);
      }
    } catch (e) { }
  }, [schools]);

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/schools`, { headers: authHeader() });
      if (res.ok) {
        const jd = await res.json();
        setSchools(jd.schools || []);
      }
    } catch (e) {
      console.error(e);
    }
    setLoadingSchools(false);
  };

  const deleteSchool = async (id) => {
    if (!window.confirm('Delete this school?')) return;
    try {
      const res = await fetch(`${apiUrl}/api/admin/schools/${id}`, { method: 'DELETE', headers: authHeader() });
      if (res.ok) {
        await fetchSchools();
        setSelectedSchool(null);
      } else {
        const txt = await res.text(); console.error('Failed delete school', res.status, txt); window.alert('Failed to delete school');
      }
    } catch (e) { console.error('deleteSchool error', e); window.alert('Failed to delete school'); }
  };

  const [showAccessDenied, setShowAccessDenied] = useState(false);
  useEffect(() => {
    if (!user) return; // don't show until auth state resolves
    if (user && (user.role !== 'Admin' && user.role !== 'Mod')) {
      setShowAccessDenied(true);
    } else {
      setShowAccessDenied(false);
    }
  }, [user]);

  if (showAccessDenied) {
    return <AccessDeniedModal show={true} />;
  }

  const roleLabel = user && user.role ? user.role : '';
  return (
    <div className="admin-wrap">
      <h2>{selectedSchool ? `Manage: ${selectedSchool.school_name} - Year 2025-2026` : `Manage Accounts (${roleLabel})`}</h2>
      <div className="admin-grid">
        {(user.role === 'Admin') && (
          <div className="left-col">
            <div className="panel-header">
              <h3>Primary Schools</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="small" onClick={fetchSchools}>Refresh</button>
                <button className="small" onClick={() => { setSelectedSchool(null); setSchoolsEditorOpen(true); }}>Add new school</button>
                <button className="small" onClick={() => { window.location.href = '/admin/school-years'; }}>Manage years & schools</button>
              </div>
            </div>
            <div className="schools-list" style={{ maxHeight: 3 * 48, overflowY: 'auto' }}>
              {loadingSchools ? (
                <div className="muted">Loading...</div>
              ) : (
                schools.map((s, idx) => (
                  <div
                    key={s.id}
                    className={`school-item ${selectedSchool && String(selectedSchool.id) === String(s.id) ? 'selected' : ''}`}
                    onClick={() => setSelectedSchool(s)}
                  >
                    <div className="school-name">
                      {editingSchoolId === s.id ? (
                        <input
                          value={editingSchoolName}
                          onChange={e => setEditingSchoolName(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        s.school_name
                      )}
                    </div>
                    <div className="school-id">
                      {(selectedSchool && String(selectedSchool.id) === String(s.id)) && (
                        <span style={{ marginLeft: 8, display: 'flex', flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                          <button className="icon-btn small" title="Edit" aria-label="Edit" onClick={(e) => { e.stopPropagation(); setSelectedSchool(s); setSchoolsEditorOpen(true); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                          </button>
                          <button className="icon-btn small" title="Delete" aria-label="Delete" onClick={(e) => { e.stopPropagation(); deleteSchool(s.id); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </span>
                      )}
                      {idx + 1}
                    </div>
                    {/* Editing/creation of schools has been moved to the School Years admin page. */}
                  </div>
                ))
              )}
            </div>
            {/* SchoolsEditor modal (hidden panel) - Add modal is opened via the header button */}
            <SchoolsEditor apiUrl={apiUrl} onSaved={fetchSchools} selected={selectedSchool} setSelected={setSelectedSchool} hidePanel={true} externalOpen={schoolsEditorOpen} onExternalClose={() => setSchoolsEditorOpen(false)} />
          </div>
        )}

        {/* Mods do not see the left-column school management panel to provide a wider workspace */}
        <div className="right-col">
          {selectedSchool ? (
            <SchoolDetail
              apiUrl={apiUrl}
              school={selectedSchool}
              refreshSchools={fetchSchools}
              initialTab={initialTabFromUrl}
            />
          ) : (
            <div className="empty-placeholder">
              Select a school on the top to manage related accounts, classes, and students.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SchoolsEditor({ apiUrl, onSaved, selected, setSelected, hidePanel = false, externalOpen = false, onExternalClose }) {
  const [name, setName] = useState('');
  const [showModal, setShowModal] = useState(false);
  useEffect(() => { setName(selected ? selected.school_name : ''); }, [selected]);
  // respond to external open requests (parent button)
  useEffect(() => {
    if (externalOpen) {
      // If parent explicitly requested opening and there's no selected school,
      // treat this as "Create new school" and clear the name. If a selected
      // school is already set (editor opened for edit), do not clear it.
      if (!selected) {
        setSelected(null);
        setName('');
      }
      setShowModal(true);
    }
  }, [externalOpen, setSelected, selected]);
  // When hidePanel is true (modal-only mode), open the modal for editing when a selected school is set
  
  const save = async () => {
    try {
      if (selected) {
        const payload = { school_name: name };
        const res = await fetch(`${apiUrl}/api/admin/schools/${selected.id}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) { onSaved(); setSelected(null); setName(''); }
      } else {
        const payload = { school_name: name };
        const res = await fetch(`${apiUrl}/api/admin/schools`, { method: 'POST', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) { onSaved(); setName(''); }
      }
    } catch (e) { console.error(e); }
    setShowModal(false);
    if (onExternalClose) onExternalClose();
  };
  const del = async () => {
    if (!selected) return;
    if (!window.confirm('Delete this school?')) return;
    try { const res = await fetch(`${apiUrl}/api/admin/schools/${selected.id}`, { method: 'DELETE', headers: authHeader() }); if (res.ok) { onSaved(); setSelected(null); setName(''); } } catch (e) { console.error(e); }
    if (onExternalClose) onExternalClose();
  };
  // when hidePanel is true, only render the modal (parent controls opening)
  if (hidePanel) {
    return (
      <Modal show={showModal} title={selected ? 'Edit school' : 'Add new school'} onClose={() => { setShowModal(false); if (onExternalClose) onExternalClose(); }}>
        <div className="form-row">
          <input style={{ width: 260 }} placeholder="School name" value={name} onChange={e => setName(e.target.value)} />
          <div className="form-actions-admin">
            <button className='btn btn-info' onClick={save} disabled={!name}>{selected ? 'Save' : 'Create'}</button>
            <button className='btn btn-danger' onClick={() => { setShowModal(false); if (onExternalClose) onExternalClose(); }}>Cancel</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <div className="card">
      <h4>{selected ? 'School Panel Action' : 'School Panel'}</h4>
      <div className="form-row">
        <div className="min-width-300">
          <button onClick={() => { setShowModal(true); setSelected(null); setName(''); }}>Add new school</button>
        </div>
        {selected && (
          <button className="icon-btn" title="Edit" aria-label="Edit" onClick={() => setShowModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
          </button>
        )}
        {selected && (
          <button className="icon-btn" title="Delete" aria-label="Delete" onClick={del}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
          </button>
        )}
      </div>
      <Modal show={showModal} title={selected ? 'School Panel Action' : 'School Panel'} onClose={() => setShowModal(false)}>
        <div className="form-row">
          <input style={{ width: 260 }} placeholder="School name" value={name} onChange={e => setName(e.target.value)} />
          <div className="form-actions-admin">
            <button className='btn btn-info' onClick={save} disabled={!name}>{selected ? 'Save' : 'Create'}</button>
            <button className='btn btn-danger' onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </div>
      </Modal>

    </div>
  );
}

function SchoolDetail({ apiUrl, school, refreshSchools, initialTab = null }) {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState(initialTab || 'teachers');
  const [teachersViewMode, setTeachersViewMode] = useState('all'); // 'all' | 'bygrade'
  // students view mode removed: we always show the StudentsEditor table and apply grade/class filters
  // Consolidated grade/class selects used by Teachers, Classes and Students tabs
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [openTeacherCreate, setOpenTeacherCreate] = useState(false);
  const [openStudentCreate, setOpenStudentCreate] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importSuccessModalOpen, setImportSuccessModalOpen] = useState(false);
  const [importSuccessMessage, setImportSuccessMessage] = useState('');
  const [importPreview, setImportPreview] = useState(null); // { header: [], rows: [{...}] }
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [classesViewMode, setClassesViewMode] = useState('all');
  const [openClassCreate, setOpenClassCreate] = useState(false);
  const [openClassEditId, setOpenClassEditId] = useState(null);
  const [openModCreate, setOpenModCreate] = useState(false);
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  // per-tab search queries (accent-insensitive)
  const [searchTeachersQ, setSearchTeachersQ] = useState('');
  const [searchClassesQ, setSearchClassesQ] = useState('');
  const [searchStudentsQ, setSearchStudentsQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [openUserEditId, setOpenUserEditId] = useState(null);
  const [openStudentEditId, setOpenStudentEditId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [uRes, cRes, sRes] = await Promise.all([
        fetch(`${apiUrl}/api/admin/users`, { headers: authHeader() }),
        fetch(`${apiUrl}/api/classes`, { headers: authHeader() }),
        // request students filtered by selected school
        fetch(`${apiUrl}/api/admin/students${school && school.id ? ('?school_id=' + school.id) : ''}`, { headers: authHeader() })
      ]);
      if (uRes.ok) { const jd = await uRes.json(); setUsers(jd.users || []); }
      if (cRes.ok) { const jd = await cRes.json(); setClasses(jd.classes || []); }
      if (sRes.ok) { const jd = await sRes.json(); setStudents(jd.students || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [school]);

  // perform the import upload when user confirms in modal
  const performImport = async () => {
    if (!importPreview || !(importPreview.rows && importPreview.rows.length)) return window.alert('No CSV to import');
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append('file', importFile);
      const forced = (tab === 'teachers') ? 'Teacher' : 'Student';
      fd.append('force_role', forced);
      if (school && school.id) fd.append('school_id', String(school.id));
      const token = localStorage.getItem('token');
      const hdr = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${apiUrl}/api/admin/import-users`, { method: 'POST', headers: hdr, body: fd });
      const jd = await res.json();
      if (res.ok) {
        setImportResult({ ok: true, data: jd });
        await fetchAll();
        setImportModalOpen(false);
        // show simple success modal with created count
        try {
          const created = (jd && jd.result && jd.result.created) ? jd.result.created : 0;
          setImportSuccessMessage(`${created} users created`);
        } catch (e) { setImportSuccessMessage('Import completed'); }
        setImportSuccessModalOpen(true);
      } else {
        setImportResult({ ok: false, error: jd && jd.message ? jd.message : 'Import failed' });
      }
    } catch (err) { setImportResult({ ok: false, error: err && err.message ? err.message : String(err) }); }
    setImporting(false);
  };

  // perform classes import upload (POST to /admin/import-classes)
  const performImportClasses = async () => {
    if (!importPreview || !(importPreview.rows && importPreview.rows.length)) return window.alert('No file to import');
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append('file', importFile);
      if (school && school.id) fd.append('school_id', String(school.id));
      const token = localStorage.getItem('token');
      const hdr = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${apiUrl}/api/admin/import-classes`, { method: 'POST', headers: hdr, body: fd });
      const jd = await res.json();
      if (res.ok) {
        setImportResult({ ok: true, data: jd });
        await fetchAll();
        setImportModalOpen(false);
        // show simple success modal summarizing classes import
        try {
          const r = jd && jd.result ? jd.result : {};
          const created = r.created || 0;
          const skipped = r.skipped || 0;
          setImportSuccessMessage(`Created: ${created}, Skipped: ${skipped}`);
        } catch (e) { setImportSuccessMessage('Classes import completed'); }
        setImportSuccessModalOpen(true);
      } else {
        setImportResult({ ok: false, error: jd && jd.message ? jd.message : 'Import failed' });
      }
    } catch (err) { setImportResult({ ok: false, error: err && err.message ? err.message : String(err) }); }
    setImporting(false);
  };


  // include all classes that belong to this school, even if teacher_id is null
  const classesForSchool = useMemo(() => classes.filter(c => String(c.school_id) === String(school.id)), [classes, school]);

  // helper: normalize string to remove diacritics and lowercase for accent-insensitive search
  const normalize = (v) => {
    if (v === null || v === undefined) return '';
    try {
      // decompose combined characters then strip combining diacritics; fallback to simple lower-case
      return String(v).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    } catch (e) {
      return String(v).normalize ? String(v).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() : String(v).toLowerCase();
    }
  };

  // derived lists filtered by school
  const teachers = useMemo(() => users.filter(u => u.role === 'Teacher' && String(u.school_id) === String(school.id)), [users, school]);
  // filtered teachers based on selected grade/class
  const filteredTeachers = useMemo(() => {
    if (!selectedGrade && !selectedClass) return teachers;
    if (selectedClass) return teachers.filter(t => String(t.class_id) === String(selectedClass));
    // when only grade selected, include teachers assigned to classes in that grade
    const classIdsInGrade = new Set((classesForSchool || []).filter(c => String(c.grade_id || 'other') === String(selectedGrade)).map(c => c.id));
    return teachers.filter(t => t.class_id && classIdsInGrade.has(t.class_id));
  }, [teachers, classesForSchool, selectedGrade, selectedClass]);
  const usersForSchool = useMemo(() => users.filter(u => String(u.school_id) === String(school.id)), [users, school]);
  // filtered classes based on selected grade and/or selected class
  const filteredClasses = useMemo(() => {
    if (!selectedGrade && !selectedClass) return classesForSchool;
    if (selectedClass) return classesForSchool.filter(c => String(c.id) === String(selectedClass));
    return classesForSchool.filter(c => String(c.grade_id || 'other') === String(selectedGrade));
  }, [classesForSchool, selectedGrade, selectedClass]);
  const studentsForSchool = useMemo(() => {
    // Prefer explicit student.school_id matching the selected school. Fallback to parent/class relations if school_id missing.
    const parentIds = new Set();
    const classIds = new Set(classesForSchool.map(c => c.id));
    return (students || []).filter(st => (
      (st.school_id && String(st.school_id) === String(school.id)) ||
      (st.class_id && classIds.has(st.class_id))
    ));
  }, [students, classesForSchool]);

  // filtered students based on selected grade and/or selected class
  const filteredStudents = useMemo(() => {
    if (!selectedGrade && !selectedClass) return studentsForSchool;
    // if class selected, filter by class
    if (selectedClass) return studentsForSchool.filter(s => String(s.class_id) === String(selectedClass));
    // if only grade selected, include students whose class belongs to that grade
    const classIdsInGrade = new Set((classesForSchool || []).filter(c => String(c.grade_id || 'other') === String(selectedGrade)).map(c => c.id));
    return studentsForSchool.filter(s => s.class_id && classIdsInGrade.has(s.class_id));
  }, [studentsForSchool, classesForSchool, selectedGrade, selectedClass]);

  // Apply per-tab search filtering (accent-insensitive)
  const teachersToShow = useMemo(() => {
    const base = (!selectedGrade && !selectedClass) ? (usersForSchool || []) : (filteredTeachers || []);
    if (!searchTeachersQ || !String(searchTeachersQ).trim()) return base;
    const q = normalize(searchTeachersQ.trim());
    return base.filter(u => {
      const fields = [u.username, u.full_name, u.first_name, u.last_name, u.identifier_code, u.email];
      return fields.some(f => normalize(f).includes(q));
    });
  }, [usersForSchool, filteredTeachers, selectedGrade, selectedClass, searchTeachersQ]);

  const classesToShow = useMemo(() => {
    const base = (!selectedGrade && !selectedClass) ? (classesForSchool || []) : (filteredClasses || []);
    if (!searchClassesQ || !String(searchClassesQ).trim()) return base;
    const q = normalize(searchClassesQ.trim());
    return base.filter(c => {
      const teacherName = c.teacher_username || ((teachers || []).find(t => String(t.id) === String(c.teacher_id)) || {}).username || '';
      return normalize(c.name).includes(q) || normalize(teacherName).includes(q);
    });
  }, [classesForSchool, filteredClasses, selectedGrade, selectedClass, searchClassesQ, teachers]);

  const studentsToShow = useMemo(() => {
    const base = (!selectedGrade && !selectedClass) ? (studentsForSchool || []) : (filteredStudents || []);
    if (!searchStudentsQ || !String(searchStudentsQ).trim()) return base;
    const q = normalize(searchStudentsQ.trim());
    return base.filter(s => {
      const fields = [s.username, s.full_name, s.first_name, s.last_name, s.identifier_code, s.parent_name];
      return fields.some(f => normalize(f).includes(q));
    });
  }, [studentsForSchool, filteredStudents, selectedGrade, selectedClass, searchStudentsQ]);

  // when classes change, initialize student grade/class selects
  useEffect(() => {
    const gradeKeys = Array.from(new Set(classesForSchool.map(c => c.grade_id || 'other'))).sort((a, b) => {
      if (a === 'other') return 1; if (b === 'other') return -1; return Number(a) - Number(b);
    });
    // default: do not auto-select a grade; start with empty selects
    setSelectedGrade(null);
    setSelectedClass(null);
  }, [classesForSchool]);
  // when a select is used, keep selectedGrade/selectedClass; Students tab will use these to filter data

  // moderator users for this school (admin-only view)
  const mods = useMemo(() => users.filter(u => u.role === 'Mod' && String(u.school_id) === String(school.id)), [users, school]);

  return (
    <div>
      <div className="panel-header">
        <div className="admin-actions">
          <button className="" onClick={() => window.location.href = "/admin/student-history"}>Student History</button>
          <button className="" onClick={() => window.location.href = "/admin/teacher-history"}>Teacher History</button>
        </div>
        {/* unified search box: visible and bound to the active tab's query */}
        <div style={{ marginLeft: 12 }}>
          <input
            className="search-input"
            placeholder={tab === 'teachers' ? 'Enter Teacher name or identifier code' : (tab === 'classes' ? 'Enter Class name' : 'Enter Student name or identifier code')}
            value={tab === 'teachers' ? searchTeachersQ : (tab === 'classes' ? searchClassesQ : searchStudentsQ)}
            onChange={e => {
              const v = e.target.value;
              if (tab === 'teachers') setSearchTeachersQ(v);
              else if (tab === 'classes') setSearchClassesQ(v);
              else setSearchStudentsQ(v);
            }}
            style={{ padding: '8px 10px', minWidth: 460, borderRadius: 8 }}
          />
        </div>
        <div className="tabs">
          <button className={tab === 'teachers' ? 'active' : ''} onClick={() => { setTab('teachers'); setSelectedGrade(null); setSelectedClass(null); }}>Teachers ({teachers.length})</button>
          <button className={tab === 'classes' ? 'active' : ''} onClick={() => { setTab('classes'); setSelectedGrade(null); setSelectedClass(null); }}>Classes ({classesForSchool.length})</button>
          <button className={tab === 'students' ? 'active' : ''} onClick={() => setTab('students')}>Students ({studentsForSchool.length})</button>
          {currentUser && currentUser.role === 'Admin' && (
            <button className={tab === 'mods' ? 'active' : ''} onClick={() => setTab('mods')}>Moderator ({mods.length})</button>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? <div className="muted">Loading...</div> : (
          <>
            <Modal show={importModalOpen} title={importPreview && importPreview.header && importPreview.header.length ? `Import preview (${tab === 'teachers' ? 'Teachers' : (tab === 'classes' ? 'Classes' : 'Students')})` : 'Import preview'} onClose={() => { setImportModalOpen(false); setImportPreview(null); }}>
              <div>
                {importPreview && importPreview.header && importPreview.header.length ? (
                  <div>
                    <div className="muted">Preview (first 50 rows)</div>
                    <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #eee', marginTop: 8 }}>
                      <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
                        <thead><tr>{importPreview.header.map(h => <th key={h}>{h}</th>)}</tr></thead>
                        <tbody>
                          {importPreview.rows.slice(0, 50).map((r, idx) => (
                            <tr key={idx}>{importPreview.header.map(h => <td key={h}>{r[h] || ''}</td>)}</tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="muted">No preview available for the selected file.</div>
                )}
                {importResult && (
                  <div style={{ marginTop: 8 }} className={importResult.ok ? 'muted' : 'error'}>
                    {tab === 'classes' ? (
                      importResult.ok ? (
                        (() => {
                          const r = importResult.data && importResult.data.result ? importResult.data.result : {};
                          return `Created: ${r.created || 0}, Skipped: ${r.skipped || 0}, Errors: ${r.errors ? r.errors.length : 0}`;
                        })()
                      ) : (`Error: ${importResult.error}`)
                    ) : (
                      importResult.ok ? `${importResult.data && importResult.data.result ? importResult.data.result.created : 0} users created` : `Error: ${importResult.error}`
                    )}
                    {/* show created classes/grades details when available */}
                    {tab === 'classes' && importResult.ok && importResult.data && importResult.data.result && (
                      <div style={{ marginTop: 6, fontSize: 12 }}>
                        {importResult.data.result.classesCreated && importResult.data.result.classesCreated.length ? (
                          <div>Classes created: {importResult.data.result.classesCreated.map(c => `${c.name}${c.id ? `(#${c.id})` : ''}`).join(', ')}</div>
                        ) : null}
                        {importResult.data.result.gradesCreated && importResult.data.result.gradesCreated.length ? (
                          <div>Grades created: {importResult.data.result.gradesCreated.map(g => `${g.name}${g.id ? `(#${g.id})` : ''}`).join(', ')}</div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                  <button className='btn btn-close' onClick={() => { setImportModalOpen(false); setImportPreview(null); }}>Cancel</button>
                  <button className='btn btn-primary' onClick={tab === 'classes' ? performImportClasses : performImport} disabled={importing || !(importPreview && importPreview.rows && importPreview.rows.length)}>{importing ? 'Importing...' : 'Import'}</button>
                </div>
              </div>
            </Modal>
            {tab === 'teachers' && (
              <div>
                <div className="tabs-row">
                  <div className="tabs-left">
                    <button className={teachersViewMode === 'all' ? 'active' : ''} onClick={() => { setTeachersViewMode('all'); setSelectedGrade(null); setSelectedClass(null); }}>All teachers</button>
                    <div style={{ display: 'inline-flex', gap: 8, marginLeft: 12, alignItems: 'center' }}>
                      <div style={{ minWidth: 160 }}>
                        <RSSelect
                          options={Array.from(new Set(classesForSchool.map(c => c.grade_id || 'other'))).sort((a, b) => { if (a === 'other') return 1; if (b === 'other') return -1; return Number(a) - Number(b); }).map(g => ({ value: g, label: g === 'other' ? 'Other' : `Grade ${g}` }))}
                          value={selectedGrade}
                          onChange={v => { setSelectedGrade(v || null); setSelectedClass(null); }}
                          emptyValue={null}
                          isClearable={true}
                          placeholder="-- Select grade --"
                        />
                      </div>
                      <div style={{ minWidth: 160 }}>
                        <RSSelect
                          options={(classesForSchool.filter(c => (selectedGrade === null || String(c.grade_id || 'other') === String(selectedGrade)))).map(cl => ({ value: cl.id, label: `Class: ${cl.name}` }))}
                          value={selectedClass}
                          onChange={v => setSelectedClass(v || null)}
                          emptyValue={null}
                          isClearable={true}
                          placeholder="-- Select class --"
                        />
                      </div>
                      {/* search moved to panel header (unified per-active-tab) */}
                      {/* search moved to panel header (unified per-active-tab) */}
                      {/* search moved to panel header (unified per-active-tab) */}
                    </div>
                  </div>
                  <div className="tabs-right">
                    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <button onClick={() => { setOpenTeacherCreate(true); }}>Create new teacher</button>
                      <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const inp = document.createElement('input');
                          inp.type = 'file';
                          inp.accept = '.csv,.xlsx,.xls';
                          inp.onchange = (ev) => {
                            const f = ev.target.files && ev.target.files[0];
                            if (!f) return;
                            setImportFile(f);
                            setImportResult(null);
                            setImportPreview(null);
                            const reader = new FileReader();
                            const name = (f.name || '').toLowerCase();
                            // For xlsx/xls use SheetJS to parse; for csv use SheetJS as well (handles CSV robustly)
                            reader.onload = (r) => {
                              try {
                                const data = r.target.result;
                                let workbook;
                                if (name.endsWith('.csv')) {
                                  // read CSV as string
                                  workbook = XLSX.read(String(data), { type: 'string' });
                                } else {
                                  // read binary for xlsx/xls
                                  const arr = new Uint8Array(data);
                                  workbook = XLSX.read(arr, { type: 'array' });
                                }
                                const firstSheetName = workbook.SheetNames && workbook.SheetNames[0];
                                const ws = workbook.Sheets[firstSheetName];
                                const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                                const header = rows && rows.length ? Object.keys(rows[0]) : (ws && ws['!ref'] ? (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || []) : []);
                                setImportPreview({ header: header || [], rows: rows || [] });
                                setImportModalOpen(true);
                              } catch (err) {
                                console.error('Failed parse file', err);
                                setImportPreview({ header: [], rows: [] });
                                setImportModalOpen(true);
                              }
                            };
                            // Read as text for csv, array buffer for xlsx
                            if (name.endsWith('.csv')) reader.readAsText(f, 'utf-8'); else reader.readAsArrayBuffer(f);
                          };
                          inp.click();
                        }}>Import CSV / XLSX</button>
                      </div>
                    </div>
                    {importResult && (
                      <div style={{ marginTop: 8 }} className={importResult.ok ? 'muted' : 'error'}>
                        {importResult.ok ? `${importResult.data && importResult.data.result ? importResult.data.result.created : 0} users created` : `Error: ${importResult.error}`}
                      </div>
                    )}


                  </div>
                </div>
                {(!selectedGrade && !selectedClass) ? (
                  <UsersEditor apiUrl={apiUrl} users={teachersToShow} schools={[school]} classes={classesForSchool} roleFilter={'Teacher'} onSaved={fetchAll} openEditId={openUserEditId} onCleared={() => setOpenUserEditId(null)} openCreate={openTeacherCreate} onCreateCleared={() => setOpenTeacherCreate(false)} />
                ) : (
                  <UsersEditor apiUrl={apiUrl} users={teachersToShow} schools={[school]} classes={classesForSchool} roleFilter={'Teacher'} onSaved={fetchAll} openEditId={openUserEditId} onCleared={() => setOpenUserEditId(null)} openCreate={openTeacherCreate} onCreateCleared={() => setOpenTeacherCreate(false)} />
                )}
              </div>
            )}
            {tab === 'classes' && (
              <div>
                <div className="tabs-row">
                  <div className="tabs-left">
                    <button className={classesViewMode === 'all' ? 'active' : ''} onClick={() => { setClassesViewMode('all'); setSelectedGrade(null); setSelectedClass(null); }}>All classes</button>
                    <div style={{ display: 'inline-flex', gap: 8, marginLeft: 12, alignItems: 'center' }}>
                      <div style={{ minWidth: 160 }}>
                        <RSSelect
                          options={Array.from(new Set(classesForSchool.map(c => c.grade_id || 'other'))).sort((a, b) => { if (a === 'other') return 1; if (b === 'other') return -1; return Number(a) - Number(b); }).map(g => ({ value: g, label: g === 'other' ? 'Other' : `Grade ${g}` }))}
                          value={selectedGrade}
                          onChange={v => { setSelectedGrade(v || null); setSelectedClass(null); }}
                          emptyValue={null}
                          isClearable={true}
                          placeholder="-- Select grade --"
                        />
                      </div>
                      <div style={{ minWidth: 160 }}>
                        <RSSelect
                          options={(classesForSchool.filter(c => (selectedGrade === null || String(c.grade_id || 'other') === String(selectedGrade)))).map(cl => ({ value: cl.id, label: `Class: ${cl.name}` }))}
                          value={selectedClass}
                          onChange={v => setSelectedClass(v || null)}
                          emptyValue={null}
                          isClearable={true}
                          placeholder="-- Select class --"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="tabs-right">
                    <button onClick={() => setOpenClassCreate(true)}>Create new class</button>
                    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', marginLeft: 8 }}>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        const inp = document.createElement('input');
                        inp.type = 'file';
                        inp.accept = '.csv,.xlsx,.xls';
                        inp.onchange = (ev) => {
                          const f = ev.target.files && ev.target.files[0];
                          if (!f) return;
                          setImportFile(f);
                          setImportResult(null);
                          setImportPreview(null);
                          const reader = new FileReader();
                          const name = (f.name || '').toLowerCase();
                          reader.onload = (r) => {
                            try {
                              const data = r.target.result;
                              let workbook;
                              if (name.endsWith('.csv')) {
                                workbook = XLSX.read(String(data), { type: 'string' });
                              } else {
                                const arr = new Uint8Array(data);
                                workbook = XLSX.read(arr, { type: 'array' });
                              }
                              const firstSheetName = workbook.SheetNames && workbook.SheetNames[0];
                              const ws = workbook.Sheets[firstSheetName];
                              const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                              const header = rows && rows.length ? Object.keys(rows[0]) : (ws && ws['!ref'] ? (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || []) : []);
                              setImportPreview({ header: header || [], rows: rows || [] });
                              setImportModalOpen(true);
                            } catch (err) {
                              console.error('Failed parse file', err);
                              setImportPreview({ header: [], rows: [] });
                              setImportModalOpen(true);
                            }
                          };
                          if (name.endsWith('.csv')) reader.readAsText(f, 'utf-8'); else reader.readAsArrayBuffer(f);
                        };
                        inp.click();
                      }}>Import CSV / XLSX</button>
                    </div>
                  </div>
                </div>
                {(!selectedGrade && !selectedClass) ? (
                  <ClassesEditor apiUrl={apiUrl} classes={classesToShow} teachers={teachers} school={school} onSaved={fetchAll} openCreate={openClassCreate} onCreateCleared={() => setOpenClassCreate(false)} openEditId={openClassEditId} onCleared={() => setOpenClassEditId(null)} />
                ) : (
                  <ClassesEditor apiUrl={apiUrl} classes={classesToShow} teachers={teachers} school={school} onSaved={fetchAll} openCreate={openClassCreate} onCreateCleared={() => setOpenClassCreate(false)} openEditId={openClassEditId} onCleared={() => setOpenClassEditId(null)} />
                )}
              </div>
            )}
            {tab === 'students' && (
              <div>
                <div className="tabs-row">
                  <div className="tabs-left">
                    <button className={tab === 'students' ? 'active' : ''} onClick={() => { setSelectedGrade(null); setSelectedClass(null); }}>{'All students'}</button>
                    <div style={{ display: 'inline-flex', gap: 8, marginLeft: 12, alignItems: 'center' }}>
                      <div style={{ minWidth: 160 }}>
                        <RSSelect
                          options={Array.from(new Set(classesForSchool.map(c => c.grade_id || 'other'))).sort((a, b) => { if (a === 'other') return 1; if (b === 'other') return -1; return Number(a) - Number(b); }).map(g => ({ value: g, label: g === 'other' ? 'Other' : `Grade ${g}` }))}
                          value={selectedGrade}
                          onChange={v => { setSelectedGrade(v || null); setSelectedClass(null); }}
                          emptyValue={null}
                          isClearable={true}
                          placeholder="-- Select grade --"
                        />
                      </div>
                      <div style={{ minWidth: 160 }}>
                        <RSSelect
                          options={(classesForSchool.filter(c => (selectedGrade === null || String(c.grade_id || 'other') === String(selectedGrade)))).map(cl => ({ value: cl.id, label: `Class: ${cl.name}` }))}
                          value={selectedClass}
                          onChange={v => setSelectedClass(v || null)}
                          emptyValue={null}
                          isClearable={true}
                          placeholder="-- Select class --"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="tabs-right">
                    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <button onClick={() => { console.debug('[AccountAdmin] create student button clicked for school', school && school.id); setOpenStudentCreate(true); }}>Create new student</button>
                      <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const inp = document.createElement('input');
                          inp.type = 'file';
                          inp.accept = '.csv,.xlsx,.xls';
                          inp.onchange = (ev) => {
                            const f = ev.target.files && ev.target.files[0];
                            if (!f) return;
                            setImportFile(f);
                            setImportResult(null);
                            setImportPreview(null);
                            const reader = new FileReader();
                            const name = (f.name || '').toLowerCase();
                            // For xlsx/xls use SheetJS to parse; for csv use SheetJS as well (handles CSV robustly)
                            reader.onload = (r) => {
                              try {
                                const data = r.target.result;
                                let workbook;
                                if (name.endsWith('.csv')) {
                                  // read CSV as string
                                  workbook = XLSX.read(String(data), { type: 'string' });
                                } else {
                                  // read binary for xlsx/xls
                                  const arr = new Uint8Array(data);
                                  workbook = XLSX.read(arr, { type: 'array' });
                                }
                                const firstSheetName = workbook.SheetNames && workbook.SheetNames[0];
                                const ws = workbook.Sheets[firstSheetName];
                                const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
                                const header = rows && rows.length ? Object.keys(rows[0]) : (ws && ws['!ref'] ? (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || []) : []);
                                setImportPreview({ header: header || [], rows: rows || [] });
                                setImportModalOpen(true);
                              } catch (err) {
                                console.error('Failed parse file', err);
                                setImportPreview({ header: [], rows: [] });
                                setImportModalOpen(true);
                              }
                            };
                            // Read as text for csv, array buffer for xlsx
                            if (name.endsWith('.csv')) reader.readAsText(f, 'utf-8'); else reader.readAsArrayBuffer(f);
                          };
                          inp.click();
                        }}>Import CSV / XLSX</button>
                      </div>
                    </div>
                    {importResult && (
                      <div style={{ marginTop: 8 }} className={importResult.ok ? 'muted' : 'error'}>
                        {importResult.ok ? `${importResult.data && importResult.data.result ? importResult.data.result.created : 0} users created` : `Error: ${importResult.error}`}
                      </div>
                    )}
                  </div>
                </div>
                <>
                  {(!selectedGrade && !selectedClass) ? (
                    <StudentsEditor apiUrl={apiUrl} students={studentsToShow} classes={classesForSchool} school={school} onSaved={fetchAll} openEditId={openStudentEditId} onCleared={() => setOpenStudentEditId(null)} openCreate={openStudentCreate} onCreateCleared={() => setOpenStudentCreate(false)} />
                  ) : (
                    <>
                      <StudentsEditor apiUrl={apiUrl} students={studentsToShow} classes={classesForSchool} school={school} onSaved={fetchAll} openEditId={openStudentEditId} onCleared={() => setOpenStudentEditId(null)} openCreate={openStudentCreate} onCreateCleared={() => setOpenStudentCreate(false)} />
                    </>
                  )}
                </>
              </div>
            )}
            {/* Import success modal: shows simple summary and a single Close button */}
            <Modal show={importSuccessModalOpen} title={'Import result'} onClose={() => setImportSuccessModalOpen(false)}>
              <div>
                <div style={{ marginBottom: 12 }}>{importSuccessMessage || 'Import completed'}</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className='btn btn-primary' onClick={() => { setImportSuccessModalOpen(false); setImportResult(null); setImportSuccessMessage(''); }}>Close</button>
                </div>
              </div>
            </Modal>
            {tab === 'mods' && currentUser && currentUser.role === 'Admin' && (
              <div>
                <div className="tabs-row">
                  <div className="tabs-left">
                    <div />
                  </div>
                  <div className="tabs-right">
                    <button onClick={() => setOpenModCreate(true)}>Create new Moderator</button>
                  </div>
                </div>
                <UsersEditor apiUrl={apiUrl} users={usersForSchool} schools={[school]} roleFilter={'Mod'} onSaved={fetchAll} openEditId={openUserEditId} onCleared={() => setOpenUserEditId(null)} openCreate={openModCreate} onCreateCleared={() => setOpenModCreate(false)} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function UsersEditor({ apiUrl, users, schools, classes, roleFilter, onSaved, openEditId, onCleared, noList, openCreate, onCreateCleared }) {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const { user: currentUser } = useAuth();
  const [editing, setEditing] = useState(null);
  const [showTeacherHistoryModal, setShowTeacherHistoryModal] = useState(false);
  const [teacherHistoryData, setTeacherHistoryData] = useState([]);
  const [teacherHistoryLoading, setTeacherHistoryLoading] = useState(false);
  const [teacherSelected, setTeacherSelected] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState(null);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', full_name: '', first_name: '', last_name: '', identifier_code: '', role: roleFilter || 'Teacher', school_id: '', student_ids: [], class_id: '', gender: 'other', email: '', avt: '/uploads/avt/avt-def.jpg', phone: '' });
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => { setList(users || []); }, [users]);
  // reset pagination when the underlying users list or role filter changes
  useEffect(() => { setPage(1); }, [users, roleFilter]);

  // fetch teacher class-assignment history for a single teacher
  async function fetchTeacherHistory(teacherId) {
    setTeacherHistoryLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/teachers/${teacherId}/history`, { headers: authHeader() });
      if (!res.ok) { setTeacherHistoryData([]); setTeacherHistoryLoading(false); return; }
      const jd = await res.json(); setTeacherHistoryData(jd.history || []);
    } catch (e) { console.error(e); setTeacherHistoryData([]); }
    setTeacherHistoryLoading(false);
  }

  function openTeacherHistory(t) {
    setTeacherSelected(t);
    setShowTeacherHistoryModal(true);
    fetchTeacherHistory(t.id);
  }

  useEffect(() => { setForm(f => ({ ...f, role: roleFilter })); }, [roleFilter]);

  const startCreate = () => { setEditing(null); setForm({ username: '', password: '', full_name: '', first_name: '', last_name: '', identifier_code: '', role: roleFilter || 'Teacher', school_id: schools[0] ? schools[0].id : '', student_ids: [], class_id: '', gender: 'other', email: '', avt: '/uploads/avt/avt-def.jpg', phone: '' }); setErrors({}); };
  const startEdit = (u) => {
    setEditing(u.id);
    const classField = (u.class_id === null) ? 'pending' : (u.class_id || '');
    // prefer explicit first_name/last_name, fallback to splitting full_name (last + first)
    let fn = '';
    let ln = '';
    if (u.first_name || u.last_name) {
      fn = u.first_name || '';
      ln = u.last_name || '';
    } else if (u.full_name) {
      const parts = String(u.full_name).trim().split(/\s+/);
      fn = parts.length ? parts.pop() : '';
      ln = parts.length ? parts.join(' ') : '';
    }
    setForm({ username: u.username, password: '', full_name: u.full_name || '', first_name: fn, last_name: ln, identifier_code: u.identifier_code || '', role: u.role, school_id: u.school_id || '', student_ids: u.student_ids || [], class_id: classField, email: u.email || '', avt: u.avt || '/uploads/avt/avt-def.jpg', gender: u.gender || 'other', phone: u.phone || '' });
    setErrors({});
  };

  // open modal when parent passes an id to edit
  useEffect(() => {
    if (!openEditId) return;
    const u = list.find(x => x.id === openEditId);
    if (u) { startEdit(u); setErrors({}); setShowModal(true); if (onCleared) onCleared(); }
  }, [openEditId]);

  // open create modal when parent signals openCreate
  useEffect(() => {
    if (!openCreate) return;
    startCreate();
    setErrors({});
    setShowModal(true);
    if (onCreateCleared) onCreateCleared();
  }, [openCreate]);

  const validate = () => {
    const e = {};
    if (!form.username || String(form.username).trim().length < 3) e.username = 'Username must be at least 3 characters';
    if (!editing && (!form.password || form.password.length < 6)) e.password = 'Password must be at least 6 characters';
    if (!form.school_id) e.school_id = 'Select a school';
    // require email for teachers
    if (form.role === 'Teacher') {
      if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Invalid email';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    try {
      if (!validate()) return;
      const payload = { ...form };
      // if current user is Mod, force school_id to the current panel school (schools[0])
      if (currentUser && currentUser.role === 'Mod') {
        const forced = (schools && schools[0] && schools[0].id) ? schools[0].id : payload.school_id;
        payload.school_id = forced;
      }
      // normalize teacher class selection: send class_id scalar (number or null)
      if (payload.role === 'Teacher') {
        if (payload.class_id === 'pending') {
          payload.class_id = null; // represent 'waiting for class' as null
        } else if (payload.class_id !== undefined && payload.class_id !== null && payload.class_id !== '') {
          payload.class_id = Number(payload.class_id) || null;
        } else {
          payload.class_id = null;
        }
      }

      // include optional fields email and avt when present
      if (form.email !== undefined) payload.email = form.email || null;
      if (form.avt !== undefined) payload.avt = form.avt || '/uploads/avt/avt-def.jpg';
      // include identifier_code explicitly
      if (form.identifier_code !== undefined) payload.identifier_code = form.identifier_code || null;
      // For Teacher role prefer first_name/last_name fields. For other roles keep full_name compatibility.
      if (payload.role === 'Teacher') {
        payload.first_name = form.first_name || null;
        payload.last_name = form.last_name || null;
        // keep a legacy full_name column for compatibility (last + ' ' + first)
        const full = `${(form.last_name || '').trim()} ${(form.first_name || '').trim()}`.trim();
        payload.full_name = full || null;
      } else {
        if (form.full_name !== undefined) payload.full_name = form.full_name || null;
      }

      if (editing) {
        const res = await fetch(`${apiUrl}/api/admin/users/${editing}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) await onSaved();
      } else {
        const res = await fetch(`${apiUrl}/api/admin/users`, { method: 'POST', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok) await onSaved();
      }
      setEditing(null); setForm({ username: '', password: '', role: roleFilter || 'Teacher', school_id: '', student_ids: [], gender: 'other' });
    } catch (e) { console.error(e); }
  };

  const del = async (id) => { if (!window.confirm('Delete this account?')) return; try { const res = await fetch(`${apiUrl}/api/admin/users/${id}`, { method: 'DELETE', headers: authHeader() }); if (res.ok) await onSaved(); } catch (e) { console.error(e); } };
  const resetUser = async (id, username, u) => {
    // decide if currentUser may see the button (frontend extra check) and then call API
    try {
      if (!window.confirm(`Reset password for ${username || ('#' + id)}? This will generate a temporary password.`)) return;
      setResetting(true);
      const res = await fetch(`${apiUrl}/api/admin/users/${id}/reset`, { method: 'POST', headers: authHeader() });
      console.debug('[UsersEditor] resetUser response status', res.status);
      if (!res.ok) {
        const txt = await res.text();
        console.debug('[UsersEditor] resetUser response body (error)', txt);
        window.alert(`Failed to reset password: ${res.status} ${txt}`);
        setResetting(false);
        return;
      }
      let jd = null;
      try { jd = await res.json(); } catch (e) { console.debug('[UsersEditor] resetUser parse json failed', e); }
      console.debug('[UsersEditor] resetUser response json', jd);
      const pw = jd && jd.password ? jd.password : null;
      if (!pw) {
        window.alert('Reset succeeded but no password was returned by the server. Check server logs.');
      }
      setResetPasswordValue(pw);
      setResetTargetUser(u || { id, username });
      setShowResetModal(true);
      // try copy automatically
      if (pw && navigator && navigator.clipboard && navigator.clipboard.writeText) {
        try { await navigator.clipboard.writeText(pw); } catch (e) { /* ignore */ }
      }
      // do NOT call onSaved() here No infomation refreshing parent immediately can remount this
      // component and clear the local modal state. Leave refreshing to user action.
    } catch (e) { console.error(e); window.alert('Unexpected error while resetting'); }
    setResetting(false);
  };
  return (
    <div>
      {!noList && (
        <>
          {/* <div className="form-row">
            <div className="min-width-300">
                <button onClick={() => { startCreate(); setShowModal(true); }}>Create new account</button>
              </div>
          </div> */}

          <table className="data-table">
            <thead>
              <tr>
                <th>Identifier Code</th>
                <th>Username</th>
                <th>Full name</th>
                {roleFilter === 'Teacher' ? <th>Class</th> : <th>Role</th>}
                <th>Year</th>
                <th>Status</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {
                (() => {
                  // filter then sort newest-first by numeric id
                  const filtered = (list || []).filter(u => u.role === roleFilter).slice().sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
                  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
                  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);
                  return pageItems.map(u => (
                    <tr key={u.id} className={u.status && u.status.toLowerCase() === 'inactive' ? 'inactive-row' : ''}>
                      <td>{u.identifier_code || u.id}</td>
                      <td>{u.username}</td>
                      <td>{(u.last_name || u.full_name || '') + ((u.first_name || '') ? (' ' + u.first_name) : '')}</td>
                      {roleFilter === 'Teacher' ? (
                        <td>{lookupClassNameInList(classes, u.class_id)}</td>
                      ) : (
                        <td>{u.role}</td>
                      )}
                      <td>2025-2026</td>
                      <td>{u.status || 'active'}</td>
                      <td className="col-actions">
                        <button className="icon-btn " title="Edit" aria-label="Edit" onClick={() => { startEdit(u); setShowModal(true); }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                        </button>
                        {roleFilter === 'Teacher' && (
                          <button className="icon-btn" title="View history" aria-label="View history" onClick={() => openTeacherHistory(u)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                          </button>
                        )}
                        {/* Reset button: visible to Admins; Mods can reset Teacher/Student in same school */}
                        {currentUser && ((currentUser.role === 'Admin' && ['Teacher', 'Mod', 'Student'].includes(u.role)) || (currentUser.role === 'Mod' && ['Teacher', 'Student'].includes(u.role) && String(u.school_id) === String(currentUser.school_id))) && (
                          <button className="icon-btn " title="Reset password" aria-label="Reset password" onClick={() => resetUser(u.id, u.username, u)} disabled={resetting}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10v6a2 2 0 0 1-2 2h-4v-4H9v-4a4 4 0 0 1 4-4h1" /><path d="M7 10V7" /></svg>
                          </button>
                        )}
                        {/* Mods are not allowed to delete users via UI per new rules */}
                        {currentUser && currentUser.role !== 'Mod' && (
                          <button className="icon-btn " title="Delete" aria-label="Delete" onClick={() => del(u.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ));
                })()
              }
            </tbody>
            {
              // pagination controls: only show when role is Teacher and there are more than one page
              (() => {
                const filtered = (list || []).filter(u => u.role === roleFilter).slice().sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
                const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
                if (roleFilter === 'Teacher' && filtered.length > pageSize) {
                  const pages = [];
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                  return (
                    <tfoot>
                      <tr>
                        <td colSpan={7}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 0' }}>
                            {pages.map(p => (
                              <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  );
                }
                return null;
              })()
            }
          </table>
        </>
      )}

      <Modal show={showModal} title={editing ? 'Edit account' : 'Create account'} onClose={() => { setShowModal(false); setEditing(null); setErrors({}); }}>
        <div className="modal-form">
          <div className="field">
            <label>Username</label>
            <input placeholder="username" value={form.username} onChange={e => { setForm({ ...form, username: e.target.value }); if (errors.username) setErrors(prev => { const p = { ...prev }; delete p.username; return p; }); }} />
            {errors.username && <div className="error">{errors.username}</div>}
          </div>
          <div className="field">
            <label>Password</label>
            <input type="password" placeholder="password" value={form.password} onChange={e => { setForm({ ...form, password: e.target.value }); if (errors.password) setErrors(prev => { const p = { ...prev }; delete p.password; return p; }); }} />
            {errors.password && <div className="error">{errors.password}</div>}
          </div>

          <div className="field">
            <label>Role</label>
            <RSSelect
              options={roleFilter ? [{ value: roleFilter, label: roleFilter }] : [
                { value: 'Teacher', label: 'Teacher' },
                { value: 'Student', label: 'Student' },
                ...(currentUser && currentUser.role === 'Admin' ? [{ value: 'Mod', label: 'Mod' }, { value: 'Admin', label: 'Admin' }] : [])
              ]}
              value={form.role || ''}
              onChange={v => { setForm({ ...form, role: v }); if (errors.role) setErrors(prev => { const p = { ...prev }; delete p.role; return p; }); }}
              emptyValue={''}
              isClearable={false}
            />
          </div>

          {errors.role && <div className="error">{errors.role}</div>}

          {/* Email and Avatar fields (show for Teacher and Mod/Admin too) */}
          <div className="field">
            <label>Email</label>
            <input placeholder="email" value={form.email || ''} onChange={e => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors(prev => { const p = { ...prev }; delete p.email; return p; }); }} />
            {errors.email && <div className="error">{errors.email}</div>}
          </div>
          <div className="field">
            <label>Phone</label>
            <input placeholder="Phone Number" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="field">
            <label>Sex</label>
            <RSSelect
              options={[{ value: 'female', label: 'Female' }, { value: 'male', label: 'Male' }, { value: 'other', label: 'Other' }]}
              value={form.gender || 'other'}
              onChange={v => setForm({ ...form, gender: v })}
              emptyValue={''}
              isClearable={false}
            />
          </div>
          <div className="field">
            <label>Last name</label>
            <input placeholder="Last name" value={form.last_name || ''} onChange={e => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div className="field">
            <label>First name</label>
            <input placeholder="First name" value={form.first_name || ''} onChange={e => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div className="field">
            <label>Avatar</label>
            <input placeholder="/uploads/avt/.." value={form.avt || '/uploads/avt/avt-def.jpg'} onChange={e => setForm({ ...form, avt: e.target.value })} />
          </div>

          <div className="field">
            <label>Identifier Code</label>
            <input placeholder="Identifier Code" value={form.identifier_code || ''} onChange={e => setForm({ ...form, identifier_code: e.target.value })} />
          </div>

          {/* If role is Teacher allow setting class via helper class_id */}
          {(form.role === 'Teacher') && (
            <div className="field">
              <label>Class</label>
              <RSSelect
                options={[{ value: '', label: '-- Select class --' }, { value: 'pending', label: 'Pending assignment' }].concat(classes.filter(c => {
                  if (!c.teacher_id) return true;
                  return String(c.teacher_id) === String(editing);
                }).map(c => ({ value: c.id, label: `Class: ${c.name}` })))}
                value={form.class_id || ''}
                onChange={v => setForm({ ...form, class_id: v })}
                emptyValue={''}
                isClearable={true}
              />
            </div>
          )}
          {/* Status field */}
          <div className="field">
            <label>Status</label>
            <RSSelect
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              value={form.status || 'active'}
              onChange={v => setForm({ ...form, status: v })}
              emptyValue={''}
              isClearable={false}
            />
          </div>

          {/* If current user is Mod, force school to selected school and don't allow change */}
          <div className="field">
            <label>School</label>
            {currentUser && currentUser.role === 'Mod' ? (
              <input readOnly value={schools && schools.length ? (schools.find(s => String(s.id) === String(form.school_id)) || {}).school_name || form.school_id : form.school_id} />
            ) : (
              <RSSelect
                options={[{ value: '', label: '-- Chọn trường --' }].concat(schools.map(sc => ({ value: sc.id, label: sc.school_name })))}
                value={form.school_id || ''}
                onChange={v => { setForm({ ...form, school_id: v }); if (errors.school_id) setErrors(prev => { const p = { ...prev }; delete p.school_id; return p; }); }}
                emptyValue={''}
                isClearable={true}
              />
            )}
          </div>
          {errors.school_id && <div className="error">{errors.school_id}</div>}

          <div className="full modal-actions">
            <button className="btn btn-primary" onClick={save}>{editing ? 'Update' : 'Save'}</button>
            <button className="btn btn-close" onClick={() => { setShowModal(false); setEditing(null); setErrors({}); }}>Cancel</button>
          </div>
        </div>
      </Modal>
      {/* Teacher history modal (inline) */}
      <Modal show={showTeacherHistoryModal} title={teacherSelected ? `History: ${(() => {
        const t = teacherSelected;
        const full = ((t.last_name || '') + ((t.first_name || '') ? (' ' + t.first_name) : '')).trim();
        return full || t.full_name || t.username || ('#' + t.id);
      })()}` : 'Teacher History'} onClose={() => { setShowTeacherHistoryModal(false); setTeacherHistoryData([]); setTeacherSelected(null); }}>
        <div>
          <div className="mt-8">
            {teacherHistoryLoading ? <div className="muted">Loading...</div> : (
              (!teacherHistoryData || teacherHistoryData.length === 0) ? <div className="muted">No assignment history</div> : (
                <table className="data-table">
                  <thead><tr><th>No.</th><th>Teacher</th><th>From Class</th><th>To Class</th><th>Assigned At</th><th>Changed By</th></tr></thead>
                  <tbody>
                    {
                      (() => {
                        // sort newest-first by assigned_at/changed_at (fallback to id) and number newest as largest
                        const sorted = (teacherHistoryData || []).slice().sort((a, b) => (Date.parse(b.assigned_at || b.changed_at) || (Number(b.id) || 0)) - (Date.parse(a.assigned_at || a.changed_at) || (Number(a.id) || 0)));
                        const total = sorted.length || 0;
                        return sorted.map((h, idx) => (
                          <tr key={h.id}>
                            <td>{total - idx}</td>
                            <td>{(() => {
                              // prefer the selected teacher object (modal context) when available
                              if (teacherSelected) {
                                const t = teacherSelected;
                                const name = ((t.last_name || t.full_name || '') + ((t.first_name || '') ? (' ' + t.first_name) : '')).trim();
                                if (name) return name;
                                if (t.full_name) return t.full_name;
                                if (t.username) return t.username;
                                if (t.id) return ('#' + t.id);
                              }
                              return h.teacher_username || h.teacher_id || 'No infomation';
                            })()}</td>
                            <td>{h.from_class_name || h.from_class_id || 'No infomation'}</td>
                            <td>{h.to_class_name || h.to_class_id || 'No infomation'}</td>
                            <td>{formatDateVNDate(h.assigned_at || h.changed_at) || 'No infomation'}</td>
                            <td>{h.changed_by_username || h.changed_by || 'No infomation'}</td>
                          </tr>
                        ));
                      })()
                    }
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      </Modal>
      {/* Reset password modal */}
      <Modal show={showResetModal} title={resetTargetUser ? `Password reset: ${resetTargetUser.username || ('#' + resetTargetUser.id)}` : 'Password reset'} onClose={() => { setShowResetModal(false); setResetPasswordValue(null); setResetTargetUser(null); }}>
        <div>
          {!resetPasswordValue ? (
            <div className="muted">No password returned from server.</div>
          ) : (
            <div>
              <div className="field"><label>Temporary password</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                  <input readOnly value={resetPasswordValue} style={{ flex: 1, padding: '8px 12px' }} />
                  <button className='btn' onClick={async () => { try { await navigator.clipboard.writeText(resetPasswordValue); window.alert('Copied to clipboard'); } catch (e) { window.prompt('Copy the password manually', resetPasswordValue); } }}>Copy</button>
                </div>
              </div>
              <div className="mt-8">
                <div className="muted">Please provide this temporary password to the user and advise them to change it after login.</div>
              </div>
            </div>
          )}
        </div>
      </Modal>

    </div>
  );
}

function lookupSchoolNameInList(schools, id) {
  if (!id) return 'No infomation';
  const s = (schools || []).find(sc => String(sc.id) === String(id));
  return s ? s.school_name : id;
}

function lookupTeacherNameInList(teachers, teacher_id, teacher_username) {
  if (teacher_username) return teacher_username;
  if (!teacher_id) return 'No infomation';
  const t = (teachers || []).find(tt => String(tt.id) === String(teacher_id));
  return t ? (t.username || ('#' + t.id)) : teacher_id;
}

function lookupClassNameInList(classes, class_id) {
  if (!class_id) return 'No infomation';
  const c = (classes || []).find(cc => String(cc.id) === String(class_id));
  return c ? `Class: ${c.name}` : class_id;
}

function lookupParentNameInList(parents, parent_id, parent_username) {
  if (parent_username) return parent_username;
  if (!parent_id) return 'No infomation';
  const p = (parents || []).find(pp => String(pp.id) === String(parent_id));
  return p ? (p.username || ('#' + p.id)) : parent_id;
}

function lookupStudentNameInList(students, student_id) {
  if (!student_id) return 'No infomation';
  const s = (students || []).find(ss => String(ss.id) === String(student_id));
  return s ? (s.name || ('#' + s.id)) : student_id;
}

function lookupGradeNameInList(grades, grade_id) {
  if (!grade_id) return 'No infomation';
  const g = (grades || []).find(gg => String(gg.id) === String(grade_id));
  if (g) return g.name || (`Grade ${g.id}`);
  return grade_id;
}

// ByGradePanel: render either grade-level overview or class-level detail for Teachers/Students
function ByGradePanel({ type, classes = [], teachers = [], students = [], parents = [], selectedGradeProp = null, selectedClassIdProp = null, onGradeChange, onClassChange, onEditUser, onEditStudent }) {
  const gradeId = selectedGradeProp;
  const classId = selectedClassIdProp;

  // classes in the selected grade
  const classesInGrade = gradeId ? (classes || []).filter(c => String(c.grade_id || 'other') === String(gradeId)) : [];

  // when a class is selected, render class-level table
  if (classId) {
    const cls = (classes || []).find(c => String(c.id) === String(classId));
    if (type === 'Teacher') {
      const list = (teachers || []).filter(t => String(t.class_id) === String(classId));
      return (
        <div>
          <h4>Class details: {cls ? cls.name : classId}</h4>
          <table className="data-table">
            <thead><tr><th>ID</th><th>Username</th><th>Full name</th><th className="col-actions">Actions</th></tr></thead>
            <tbody>
              {list.map(u => (
                <tr key={u.id}><td>{u.id}</td><td>{u.username}</td><td>{u.full_name || 'No infomation'}</td><td className="col-actions"><button className="icon-btn" title="Edit user" aria-label="Edit user" onClick={() => onEditUser && onEditUser(u.id)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg></button></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    // Student view for a class
    const sList = (students || []).filter(s => String(s.class_id) === String(classId));
    return (
      <div>
        <h4>Class details: {cls ? cls.name : classId}</h4>
        <table className="data-table">
          <thead><tr><th>ID</th><th>Students</th><th>Class</th><th className="col-actions">Actions</th></tr></thead>
          <tbody>
            {sList.map(s => (
              <tr key={s.id}><td>{s.id}</td><td>{s.name || ('#' + s.id)}</td><td>{lookupClassNameInList(classes, s.class_id)}</td><td className="col-actions"><button className="icon-btn" title="Edit student" aria-label="Edit student" onClick={() => onEditStudent && onEditStudent(s.id)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg></button></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // grade-level overview
  if (gradeId) {
    if (type === 'Teacher') {
      return (
        <div>
          <h4>Teachers by grade: {gradeId === 'other' ? 'Other' : `Grade ${gradeId}`}</h4>
          <table className="data-table">
            <thead><tr><th>Class</th><th>Teacher</th><th className="col-actions">Actions</th></tr></thead>
            <tbody>
              {classesInGrade.map(cl => (
                <tr key={cl.id}><td>{cl.name}</td><td>{cl.teacher_username || 'No infomation'}</td><td className="col-actions"><button className="icon-btn" title="View class" aria-label="View class" onClick={() => onClassChange && onClassChange(String(cl.id))}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 12H3" /><path d="M21 12a9 9 0 1 0-9 9" /></svg></button></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Student grade-level table: list all students whose class is in this grade
    const classIds = new Set(classesInGrade.map(c => c.id));
    const sList = (students || []).filter(s => s.class_id && classIds.has(s.class_id));
    return (
      <div>
        <h4>Students by grade: {gradeId === 'other' ? 'Other' : `Grade ${gradeId}`}</h4>
        <table className="data-table">
          <thead><tr><th>ID</th><th>Full name</th><th>Class</th><th className="col-actions">Actions</th></tr></thead>
          <tbody>
            {sList.map(s => (
              <tr key={s.id}><td>{s.id}</td><td>{s.name || ('#' + s.id)}</td><td>{lookupClassNameInList(classes, s.class_id)}</td><td className="col-actions"><button className="icon-btn" title="Edit student" aria-label="Edit student" onClick={() => onEditStudent && onEditStudent(s.id)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg></button></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // fallback: nothing selected
  return <div className="muted">Select a grade or class to view data</div>;
}

// Minimal GradeClassesPanel: show classes grouped by grade and allow edit callback
function GradeClassesPanel({ apiUrl, classes = [], teachers = [], onEditClass }) {
  const [grades, setGrades] = useState([]);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/grades`, { headers: authHeader() });
        if (res.ok) {
          const jd = await res.json(); if (mounted) setGrades(jd.grades || []);
        }
      } catch (e) { console.error('failed load grades', e); }
    })();
    return () => { mounted = false; };
  }, [apiUrl]);

  const gradeKeys = Array.from(new Set(classes.map(c => c.grade_id || 'other'))).sort((a, b) => {
    if (a === 'other') return 1;
    if (b === 'other') return -1;
    return Number(a) - Number(b);
  });
  const classesByGrade = {};
  classes.forEach(c => { const g = c.grade_id || 'other'; if (!classesByGrade[g]) classesByGrade[g] = []; classesByGrade[g].push(c); });
  return (
    <div>
      <div className="tabs tabs-wrap">
        {gradeKeys.length ? gradeKeys.map(g => (
          <div key={g} className="grade-block">
            <div className="grade-title">{g === 'other' ? 'Other' : lookupGradeNameInList(grades, g)}</div>
            <div className="grade-classes">
              {(classesByGrade[g] || []).map(cl => (
                <div key={cl.id} className="grade-class-item">
                  <div>{cl.name}</div>
                  <div className="muted">Teacher: {cl.teacher_username || 'No infomation'}</div>
                  <div className="actions">
                    <button className="icon-btn" title="Edit class" aria-label="Edit class" onClick={() => onEditClass && onEditClass(cl.id)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )) : <div className="muted">No classes</div>}
      </div>
    </div>
  );
}

// ClassesEditor: list classes and provide create/edit modal
function ClassesEditor({ apiUrl, classes = [], teachers = [], school, onSaved, openCreate, onCreateCleared, openEditId, onCleared, noList }) {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', grade_id: '', teacher: '' });
  const [showModal, setShowModal] = useState(false);
  useEffect(() => { setList(classes || []); }, [classes]);
  useEffect(() => { setPage(1); }, [classes]);

  const startCreate = () => { setEditing(null); setForm({ name: '', grade_id: '', teacher: '' }); setShowModal(true); };
  const startEdit = (c) => { setEditing(c.id); setForm({ name: c.name || '', grade_id: c.grade_id || '', teacher: c.teacher_id || c.teacher_username || '' }); setShowModal(true); };

  useEffect(() => { if (!openEditId) return; const c = (list || []).find(x => x.id === openEditId); if (c) { startEdit(c); if (onCleared) onCleared(); } }, [openEditId]);
  useEffect(() => { if (!openCreate) return; startCreate(); if (onCreateCleared) onCreateCleared(); }, [openCreate]);

  const [grades, setGrades] = useState([]);
  useEffect(() => {
    let mounted = true;
    (async () => { try { const res = await fetch(`${apiUrl}/api/grades`, { headers: authHeader() }); if (res.ok) { const jd = await res.json(); if (mounted) setGrades(jd.grades || []); } } catch (e) { console.error('failed load grades', e); } })();
    return () => { mounted = false; };
  }, [apiUrl]);

  const save = async () => {
    try {
      const payload = { name: form.name, grade_id: form.grade_id || null, teacher: form.teacher || null, school_id: school ? school.id : null };
      if (editing) {
        const res = await fetch(`${apiUrl}/api/classes/${editing}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok && onSaved) await onSaved();
      } else {
        const res = await fetch(`${apiUrl}/api/classes`, { method: 'POST', headers: authHeader(), body: JSON.stringify(payload) });
        if (res.ok && onSaved) await onSaved();
      }
    } catch (e) { console.error(e); }
    setShowModal(false);
  };

  const del = async (id) => { if (!window.confirm('Delete this class?')) return; try { const res = await fetch(`${apiUrl}/api/classes/${id}`, { method: 'DELETE', headers: authHeader() }); if (res.ok && onSaved) await onSaved(); } catch (e) { console.error(e); } };

  return (
    <div>
      {!noList && (
        <>
          <table className="data-table">
            <thead><tr><th>No.</th><th>Year</th><th>Name</th><th>Grade</th><th>Teacher</th><th className="col-actions">Actions</th></tr></thead>
            <tbody>
              {
                (() => {
                  // sort newest first (assume numeric id increasing) and paginate
                  const filtered = (list || []).slice().sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
                  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
                  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);
                  return pageItems.map((c, idx) => {
                    // newest-first list is shown at the top; compute a sequential number
                    // where the newest (top) receives the largest number: total - globalIndex
                    const total = (filtered || []).length || 0;
                    const globalIndex = (page - 1) * pageSize + idx; // 0-based index in the sorted list
                    const number = total - globalIndex; // newest (top) -> largest number
                    return (
                      <tr key={c.id}>
                        <td>{number}</td>
                        <td>2025-2026</td>
                        <td>{c.name}</td>
                        <td>{lookupGradeNameInList(grades, c.grade_id) || 'No information'}</td>
                        <td>{
                          // prefer last_name + first_name from teacher object if available
                          (() => {
                            const t = (teachers || []).find(tt => String(tt.id) === String(c.teacher_id));
                            if (!t) return lookupTeacherNameInList(teachers, c.teacher_id, c.teacher_username);
                            const name = (t.last_name || t.full_name || '') + ((t.first_name || '') ? (' ' + t.first_name) : '');
                            return name || (t.username || ('#' + t.id));
                          })()
                        }</td>
                        <td className="col-actions">
                          <button className="icon-btn" title="Edit" aria-label="Edit" onClick={() => { startEdit(c); setShowModal(true); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                          </button>
                          <button className="icon-btn" title="Delete class" aria-label="Delete class" onClick={() => del(c.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </td>
                      </tr>
                    );
                  });
                })()
              }
            </tbody>
            {
              (() => {
                const filtered = list || [];
                const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
                if (filtered.length > pageSize) {
                  const pages = [];
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                  return (
                    <tfoot>
                      <tr>
                        <td colSpan={5}>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 0' }}>
                            {pages.map(p => (
                              <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  );
                }
                return null;
              })()
            }
          </table>
        </>
      )}
      <Modal show={showModal} title={editing ? 'Edit class' : 'Create class'} onClose={() => setShowModal(false)}>
        <div className="modal-form">
          <div className="field"><label>Class name</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div className="field"><label>Grade</label>
            <RSSelect
              options={[{ value: '', label: '-- Select grade --' }].concat((grades || []).map(g => ({ value: g.id, label: `Grade: ${g.name}` })))}
              value={form.grade_id || ''}
              onChange={v => setForm({ ...form, grade_id: v })}
              emptyValue={''}
              isClearable={true}
            />
          </div>
          <div className="field"><label>Teacher</label>
            <RSSelect
              options={[{ value: '', label: '-- None --' }].concat((teachers || []).map(t => ({ value: t.id, label: (((t.last_name || t.full_name || '') + ((t.first_name || '') ? (' ' + t.first_name) : '')) || t.username) })))}
              value={form.teacher || ''}
              onChange={v => setForm({ ...form, teacher: v })}
              emptyValue={''}
              isClearable={true}
            />
          </div>
          <div className="full modal-actions">
            <button className="btn btn-primary" onClick={save}>{editing ? 'Update' : 'Save'}</button>
            <button className="btn btn-close" onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function StudentsEditor({ apiUrl, students, classes, school, onSaved, openEditId, onCleared, noList, openCreate, onCreateCleared }) {
  const [list, setList] = useState([]);
  const { user: currentUser } = useAuth();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [editing, setEditing] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState('class'); // 'class' | 'attendance'
  const [historyStudent, setHistoryStudent] = useState(null);
  const [showResetModalStudent, setShowResetModalStudent] = useState(false);
  const [resetPasswordStudent, setResetPasswordStudent] = useState(null);
  const [resetTargetStudent, setResetTargetStudent] = useState(null);
  const [resettingStudent, setResettingStudent] = useState(false);
  const [form, setForm] = useState({
    username: '',
    password: '',
    // prefer explicit first/last name fields; keep full_name for fallback/compat
    first_name: '',
    last_name: '',
    full_name: '',
    email: '',
    phone: '',
    gender: 'other',
    parent_name: '',
    avt: '/uploads/avt/avt-def.jpg',
    class_id: '',
    school_id: school ? school.id : '',
    identifier_code: '',
    status: 'active'
  });
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => { setList(students || []); }, [students]);
  useEffect(() => { setPage(1); }, [students]);

  // fetch a single student's class-history
  async function fetchStudentHistory(studentId) {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/students/${studentId}/history`, { headers: authHeader() });
      if (!res.ok) { setHistoryData([]); setHistoryLoading(false); return; }
      const jd = await res.json(); setHistoryData(jd.history || []);
    } catch (e) { console.error(e); setHistoryData([]); }
    setHistoryLoading(false);
  }

  // fetch a single student's attendance
  async function fetchStudentAttendance(studentId) {
    setHistoryLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/students/${studentId}/attendance`, { headers: authHeader() });
      if (!res.ok) { setAttendanceData([]); setHistoryLoading(false); return; }
      const jd = await res.json(); setAttendanceData(jd.attendance || []);
    } catch (e) { console.error(e); setAttendanceData([]); }
    setHistoryLoading(false);
  }

  function openHistory(s) {
    setHistoryStudent(s);
    setShowHistoryModal(true);
    setHistoryTab('class');
    // load both datasets so switching tab is instant
    fetchStudentHistory(s.id);
    fetchStudentAttendance(s.id);
  }

  // open modal when parent passes an id to edit
  useEffect(() => {
    console.debug('[StudentsEditor] openEditId changed', openEditId);
    if (!openEditId) return;
    const s = (list || []).find(x => x.id === openEditId);
    if (s) { startEdit(s); setErrors({}); setShowModal(true); if (onCleared) onCleared(); }
  }, [openEditId]);

  // open create modal when parent signals openCreate
  useEffect(() => {
    console.debug('[StudentsEditor] openCreate changed', openCreate);
    if (!openCreate) return;
    startCreate();
    setErrors({});
    setShowModal(true);
    if (onCreateCleared) onCreateCleared();
  }, [openCreate]);

  const startCreate = () => {
    setEditing(null);
    setForm({
      username: '',
      password: '',
      first_name: '',
      last_name: '',
      full_name: '',
      email: '',
      gender: 'other',
      parent_name: '',
      avt: '/uploads/avt/avt-def.jpg',
      class_id: '',
      school_id: school ? school.id : '',
      identifier_code: generateIdentifierCode(),
      status: 'active'
    });
    setErrors({});
    setShowModal(true);
  };

  const startEdit = (s) => {
    setEditing(s.id);
    // prefer explicit first_name/last_name, fallback to splitting full_name
    let fn = '';
    let ln = '';
    if (s.first_name || s.last_name) {
      fn = s.first_name || '';
      ln = s.last_name || '';
    } else if (s.full_name) {
      const parts = String(s.full_name).trim().split(/\s+/);
      fn = parts.length ? parts.pop() : '';
      ln = parts.length ? parts.join(' ') : '';
    }
    setForm({
      username: s.username || '',
      first_name: fn,
      last_name: ln,
      full_name: s.full_name || '',
      password: '', // Do not prefill password for security reasons
      email: s.email || '',
      phone: s.phone || '',
      gender: s.gender || 'other',
      parent_name: s.parent_name || '',
      avt: s.avt || '/uploads/avt/avt-def.jpg',
      class_id: s.class_id || '',
      school_id: s.school_id || school.id,
      identifier_code: s.identifier_code || generateIdentifierCode(),
      status: s.status || 'active'
    });
    setErrors({});
    setShowModal(true);
  };

  const generateIdentifierCode = () => {
    const currentYear = new Date().getFullYear();
    const baseCode = `${currentYear}000001`;
    const maxCode = Math.max(...list.map(s => parseInt(s.identifier_code || baseCode, 10)), parseInt(baseCode, 10));
    return (maxCode + 1).toString();
  };

  const validate = () => {
    const e = {};
    if (!form.username || form.username.trim().length < 3) e.username = 'Username must be at least 3 characters';
    if (!editing && (!form.password || form.password.length < 6)) e.password = 'Password must be at least 6 characters';
    if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Invalid email';
    if (!form.school_id) e.school_id = 'Select a school';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    const payload = { ...form, role: 'Student' };
    // prefer sending explicit first_name/last_name and keep legacy full_name
    payload.first_name = form.first_name || null;
    payload.last_name = form.last_name || null;
    const legacyFull = `${(form.last_name || '').trim()} ${(form.first_name || '').trim()}`.trim();
    payload.full_name = legacyFull || (form.full_name || null);

    // ensure class_id is numeric or null; treat 'pending' as null
    if (payload.class_id === 'pending') payload.class_id = null;
    else payload.class_id = (payload.class_id !== undefined && payload.class_id !== null && payload.class_id !== '') ? (Number(payload.class_id) || null) : null;
    try {
      if (editing) {
        // For student updates, call the students-specific admin endpoint so the server
        // can record class-change history. Using /api/admin/users may bypass that logic.
        const endpoint = `${apiUrl}/api/admin/students/${editing}`;
        const res = await fetch(endpoint, {
          method: 'PUT',
          headers: authHeader(),
          body: JSON.stringify(payload)
        });
        if (res.ok) await onSaved();
      } else {
        // Creating a student should use the students endpoint
        const endpoint = `${apiUrl}/api/admin/students`;
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: authHeader(),
          body: JSON.stringify(payload)
        });
        if (res.ok) await onSaved();
      }
      setShowModal(false);
    } catch (e) {
      console.error(e);
    }
  };

  const delStudent = async (id) => {
    if (!window.confirm('Delete this student?')) return;
    try {
      const res = await fetch(`${apiUrl}/api/admin/students/${id}`, { method: 'DELETE', headers: authHeader() });
      if (res.ok && onSaved) await onSaved();
    } catch (e) { console.error(e); }
  };

  return (
    <div>
      {!noList && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Identifier Code</th>
              <th>Username</th>
              <th>Full name</th>
              <th>Class</th>
              <th>Year</th>
              <th>Status</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {
              (() => {
                // sort students newest-first by numeric id
                const filtered = (list || []).slice().sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
                const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
                const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);
                return pageItems.map(s => (
                  <tr key={s.id}>
                    <td>{s.identifier_code || s.id}</td>
                    <td>{s.username}</td>
                    <td>{(s.last_name || s.full_name || '') + ((s.first_name || '') ? (' ' + s.first_name) : '')}</td>
                    <td>{lookupClassNameInList(classes, s.class_id)}</td>
                    <td>2025-2026</td>
                    <td>{s.status}</td>
                    <td className="col-actions">
                      <button className="icon-btn" title="Edit student" aria-label="Edit student" onClick={() => startEdit(s)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                      </button>
                      <button className="icon-btn" title="View history" aria-label="View history" onClick={() => openHistory(s)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                      </button>
                      {currentUser && ((currentUser.role === 'Admin') || (currentUser.role === 'Mod' && String(s.school_id) === String(currentUser.school_id))) && (
                        <button className="icon-btn" title="Reset password" aria-label="Reset password" onClick={async () => {
                          if (!window.confirm(`Reset password for ${s.username || ('#' + s.id)}?`)) return;
                          try {
                            setResettingStudent(true);
                            const res = await fetch(`${apiUrl}/api/admin/users/${s.id}/reset`, { method: 'POST', headers: authHeader() });
                            console.debug('[StudentsEditor] reset student response status', res.status);
                            if (!res.ok) { const txt = await res.text(); console.debug('[StudentsEditor] reset student error body', txt); window.alert(`Reset failed: ${res.status} ${txt}`); setResettingStudent(false); return; }
                            let jd = null;
                            try { jd = await res.json(); } catch (e) { console.debug('[StudentsEditor] failed parse json', e); }
                            console.debug('[StudentsEditor] reset student response json', jd);
                            const pw = jd && jd.password ? jd.password : null;
                            if (!pw) window.alert('Reset succeeded but no password returned by server.');
                            setResetPasswordStudent(pw); setResetTargetStudent(s); setShowResetModalStudent(true);
                            if (pw && navigator && navigator.clipboard && navigator.clipboard.writeText) { try { await navigator.clipboard.writeText(pw); } catch (e) { } }
                            // do NOT call onSaved() here No infomation refreshing parent immediately can remount this
                            // component and clear the local modal state. Leave refreshing to user action.
                          } catch (e) { console.error(e); window.alert('Unexpected error'); }
                          setResettingStudent(false);
                        }} disabled={resettingStudent}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10v6a2 2 0 0 1-2 2h-4v-4H9v-4a4 4 0 0 1 4-4h1" /><path d="M7 10V7" /></svg>
                        </button>
                      )}
                      {currentUser && currentUser.role !== 'Mod' && (
                        <button className="icon-btn" title="Delete student" aria-label="Delete student" onClick={() => delStudent(s.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ));
              })()
            }
          </tbody>
          {
            (() => {
              const filtered = list || [];
              const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
              if (filtered.length > pageSize) {
                const pages = [];
                for (let i = 1; i <= totalPages; i++) pages.push(i);
                return (
                  <tfoot>
                    <tr>
                      <td colSpan={6}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 0' }}>
                          {pages.map(p => (
                            <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                );
              }
              return null;
            })()
          }
        </table>
      )}

      <Modal show={showModal} title={editing ? 'Edit student' : 'Create new student'} onClose={() => setShowModal(false)}>
        <div className="modal-form">
          <div className="field">
            <label>Username</label>
            <input
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
            />
            {errors.username && <div className="error">{errors.username}</div>}
          </div>
          <div className="field">
            <label>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
            />
            {errors.password && <div className="error">{errors.password}</div>}
          </div>
          <div className="field">
            <label>Email</label>
            <input
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
            />
            {errors.email && <div className="error">{errors.email}</div>}
          </div>
          <div className="field">
            <label>Sex</label>
            <RSSelect
              options={[{ value: 'female', label: 'Female' }, { value: 'male', label: 'Male' }, { value: 'other', label: 'Other' }]}
              value={form.gender || 'other'}
              onChange={v => setForm({ ...form, gender: v })}
              emptyValue={''}
              isClearable={false}
            />
          </div>
          <div className="field">
            <label>Last name</label>
            <input
              placeholder="Last name"
              value={form.last_name || ''}
              onChange={e => setForm({ ...form, last_name: e.target.value })}
            />
          </div>
          <div className="field">
            <label>First name</label>
            <input
              placeholder="First name"
              value={form.first_name || ''}
              onChange={e => setForm({ ...form, first_name: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Parent Name</label>
            <input
              value={form.parent_name}
              onChange={e => setForm({ ...form, parent_name: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Avatar</label>
            <input
              value={form.avt}
              onChange={e => setForm({ ...form, avt: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Class</label>
            <RSSelect
              options={[{ value: '', label: '-- Select class --' }, { value: 'pending', label: 'Pending assignment' }].concat((classes || []).map(c => ({ value: c.id, label: `Class: ${c.name}` })))}
              value={form.class_id}
              onChange={v => setForm({ ...form, class_id: v })}
              emptyValue={''}
              isClearable={true}
            />
          </div>
          <div className="field">
            <label>Status</label>
            <RSSelect
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              value={form.status}
              onChange={v => setForm({ ...form, status: v })}
              emptyValue={''}
              isClearable={false}
            />
          </div>
          <div className="field">
            <label>Identifier Code</label>
            <input
              value={form.identifier_code}
              onChange={e => setForm({ ...form, identifier_code: e.target.value })}
            />
          </div>
          <div className="full modal-actions">
            <button className='btn btn-primary' onClick={save}>{editing ? 'Update' : 'Save'}</button>
            <button className='btn btn-close' onClick={() => setShowModal(false)}>Cancel</button>
          </div>
        </div>
      </Modal>
      {/* Student history modal (inline) */}
      <Modal show={showHistoryModal} title={historyStudent ? `History: ${historyStudent.full_name || historyStudent.username || ('#' + historyStudent.id)}` : 'Student History'} onClose={() => { setShowHistoryModal(false); setHistoryData([]); setAttendanceData([]); setHistoryStudent(null); }}>
        <div>
          <div className="tabs">
            <button className={historyTab === 'class' ? 'active' : ''} onClick={() => setHistoryTab('class')}>Class History</button>
            <button className={historyTab === 'attendance' ? 'active' : ''} onClick={() => setHistoryTab('attendance')}>Attendance History</button>
          </div>

          <div className="mt-8">
            {historyLoading ? <div className="muted">Loading...</div> : (
              historyTab === 'class' ? (
                <div>
                  {(!historyData || historyData.length === 0) ? <div className="muted">No class transfer history</div> : (
                    <table className="data-table">
                      <thead><tr><th>No.</th><th>Student</th><th>From Class</th><th>To Class</th><th>Changed At</th><th>Changed By</th></tr></thead>
                      <tbody>
                        {
                          (() => {
                            // sort newest-first by changed_at (fallback to id) and number newest as largest
                            const sorted = (historyData || []).slice().sort((a, b) => (Date.parse(b.changed_at || b.assigned_at) || (Number(b.id) || 0)) - (Date.parse(a.changed_at || a.assigned_at) || (Number(a.id) || 0)));
                            const total = sorted.length || 0;
                            return sorted.map((h, idx) => (
                              <tr key={h.id}>
                                <td>{total - idx}</td>
                                <td>{(() => {
                                  // prefer the currently-opened student (modal context) when available
                                  if (historyStudent) {
                                    const s = historyStudent;
                                    const name = ((s.last_name || s.full_name || '') + ((s.first_name || '') ? (' ' + s.first_name) : '')).trim();
                                    if (name) return name;
                                    if (s.full_name) return s.full_name;
                                    if (s.username) return s.username;
                                    if (s.id) return ('#' + s.id);
                                  }
                                  return h.student_name || h.student_id || 'No infomation';
                                })()}</td>
                                <td>{h.from_class_name || h.from_class_id || 'No infomation'}</td>
                                <td>{h.to_class_name || h.to_class_id || 'No infomation'}</td>
                                <td>{formatDateVNDate(h.changed_at) || 'No infomation'}</td>
                                <td>{h.changed_by_username || h.changed_by || 'No infomation'}</td>
                              </tr>
                            ));
                          })()
                        }
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
                <div>
                  {(!attendanceData || attendanceData.length === 0) ? <div className="muted">No attendance history</div> : (
                    <div className="attendance-history">
                      {attendanceData.map((a, idx) => {
                        const rows = a.attendance_history || [];
                        const total = rows.length;
                        const presentCount = rows.filter(r => String((r.status || '').toLowerCase()) === 'present').length;
                        const absentCount = total - presentCount;
                        return (
                          <div key={idx} className="attendance-class-card mb-8">
                            <div className="attendance-class-header">
                              <div>
                                <div className="class-name">{a.to_class_name || a.to_class_id || 'No infomation'}</div>
                                <div className="muted">Updated: {formatDateVNDate(a.changed_at || a.assigned_at) || 'No infomation'}</div>
                              </div>
                              <div className="attendance-summary">
                                <span className="chip present">Present: {presentCount}</span>
                                <span className="chip absent">Absent: {absentCount}</span>
                              </div>
                            </div>

                            {total === 0 ? (
                              <div className="muted">No attendance information for this class</div>
                            ) : (
                              <div className="attendance-weeks">
                                {rows.map((ah, i) => {
                                  const st = String((ah.status || '').toLowerCase());
                                  const isPresent = st === 'present';
                                  const statusLabel = isPresent ? 'Present' : (ah.status || 'No infomation');
                                  return (
                                    <div key={i} className="attendance-week-row">
                                      <div className="week">{ah.week || '-'}</div>
                                      <div className="date">{ah.date || '-'}</div>
                                      <div className={`status ${isPresent ? 'present' : 'absent'}`}>{statusLabel}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </Modal>
      {/* Student reset modal (shows temporary password after reset) */}
      <Modal show={showResetModalStudent} title={resetTargetStudent ? `Password reset: ${resetTargetStudent.username || ('#' + resetTargetStudent.id)}` : 'Password reset'} onClose={() => { setShowResetModalStudent(false); setResetPasswordStudent(null); setResetTargetStudent(null); }}>
        <div>
          {!resetPasswordStudent ? (
            <div className="muted">No password returned from server.</div>
          ) : (
            <div>
              <div className="field"><label>Temporary password</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                  <input readOnly value={resetPasswordStudent} style={{ flex: 1, padding: '8px 12px' }} />
                  <button className='btn' onClick={async () => { try { await navigator.clipboard.writeText(resetPasswordStudent); window.alert('Copied to clipboard'); } catch (e) { window.prompt('Copy the password manually', resetPasswordStudent); } }}>Copy</button>
                </div>
              </div>
              <div className="mt-8">
                <div className="muted">Please provide this temporary password to the student and advise them to change it after login.</div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
